import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaTimes, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { getSubscriberProfile, createManualTransaction, getServiceProviders, getCities, getHospitalsByCity } from '../services/subscriberWalletService';

// Format date to DD-MMM-YYYY (e.g., 22-Nov-2025)
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let date;
    
    // If it's a date range (e.g., "2025-05-16 to 2026-05-16" or "16/05/2025 - 16/05/2026")
    if (dateString.includes(' to ') || dateString.includes(' - ')) {
      const separator = dateString.includes(' to ') ? ' to ' : ' - ';
      const [startDate, endDate] = dateString.split(separator);
      const formattedStart = formatDate(startDate.trim());
      const formattedEnd = formatDate(endDate.trim());
      return `${formattedStart} - ${formattedEnd}`;
    }
    
    // Handle DD/MM/YYYY or DD/MM/YY format
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      const parts = dateString.split('/');
      const day = parts[0].padStart(2, '0');
      const monthNum = parts[1].padStart(2, '0');
      let year = parts[2];
      
      // If year is 2 digits, convert to 4 digits
      if (year.length === 2) {
        const yearNum = parseInt(year, 10);
        year = yearNum > 50 ? `19${year}` : `20${year}`;
      }
      
      // Create date object to get month name
      date = new Date(`${year}-${monthNum}-${day}`);
      if (!isNaN(date.getTime())) {
        const month = monthNames[date.getMonth()];
        return `${day}-${month}-${year}`;
      }
    }
    
    // Try to parse the date
    date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  } catch {
    return 'N/A';
  }
};

const ManualTransactionProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State for API data
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoadingDataRef = useRef(false);
  
  // Auto-detect role from authentication
  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance', 
      'wallet_ops': 'Ops',
      'wallet_support': 'Support'
    };
    return roleMapping[authRole] || null;
  })();
  
  const isFinance = currentRole === 'Finance';
  const isSupport = currentRole === 'Support';
  const isSuperAdmin = currentRole === 'SuperAdmin';
  
  // Check if user has access (Ops, Finance, and SuperAdmin)
  const canAccess = currentRole === 'Ops' || currentRole === 'Finance' || currentRole === 'SuperAdmin';
  
  const [showPhone, setShowPhone] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    serviceType: '',
    selectedCity: '',       // For hospital types - stores city name
    redeemableCashback: '',
    thirdPartyTransactionId: '',
    serviceProvider: '',
    ayushpayCommission: '',
    price: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Service providers state
  const [serviceProviders, setServiceProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providerError, setProviderError] = useState(null);
  const [providerSearchText, setProviderSearchText] = useState('');
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  const providerInputRef = useRef(null);
  const providerDropdownRef = useRef(null);
  
  // Cities and hospitals state (for hospital types)
  const [cities, setCities] = useState([]); // Array of city names
  const [filteredCities, setFilteredCities] = useState([]); // Filtered cities for dropdown
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityError, setCityError] = useState(null);
  const [citySearchText, setCitySearchText] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const citySearchTimeoutRef = useRef(null);
  const cityInputRef = useRef(null);
  const cityDropdownRef = useRef(null);
  
  // Check if service type requires state/city selection
  const isHospitalType = formData.serviceType === 'hospitalization' || formData.serviceType === 'hospital_cash_allowance';
  
  // Notification state
  const [notification, setNotification] = useState(null);
  const [mounted, setMounted] = useState(false);
  
  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 5000); // Auto-dismiss after 5 seconds
  };
  
  // Mount state for portal
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Masked phone number
  const maskedPhone = React.useMemo(() => {
    const phoneNumber = profile?.personal?.mobile || profile?.phoneFull;
    if (!phoneNumber) return '';
    
    // Extract digits only
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length < 10) return phoneNumber;
    
    // Format as +91-XX-XXXX-XXXX
    if (digits.length === 10) {
      return `+91-${digits.slice(0, 2)}-****-${digits.slice(6)}`;
    } else if (digits.length === 12 && digits.startsWith('91')) {
      return `+91-${digits.slice(2, 4)}-****-${digits.slice(8)}`;
    } else {
      // Fallback - show first 2 and last 4 digits
      return `+91-${digits.slice(0, 2)}-****-${digits.slice(-4)}`;
    }
  }, [profile?.personal?.mobile, profile?.phoneFull]);


  // Fetch cities list (for hospital types) with optional search
  const fetchCities = async (searchText = '') => {
    setLoadingCities(true);
    setCityError(null);

    try {
      const response = await getCities(searchText, currentRole);
      
      // Handle different response structures
      let citiesList = [];
      if (response && response.metadata && response.metadata.api_status === true) {
        citiesList = response.msg?.items || response.msg || [];
      } else if (response?.items && Array.isArray(response.items)) {
        citiesList = response.items;
      } else if (Array.isArray(response)) {
        citiesList = response;
      } else if (response?.msg && Array.isArray(response.msg)) {
        citiesList = response.msg;
      } else if (response?.data && Array.isArray(response.data)) {
        citiesList = response.data;
      }
      
      // API returns objects like {city: "Mumbai", state: "Maharashtra"}
      // Extract just city names and remove duplicates
      const cityNames = citiesList
        .filter(city => city && typeof city === 'object' && city.city)
        .map(city => city.city)
        .filter((city, index, self) => self.indexOf(city) === index); // Remove duplicates
      
      // Sort by city name
      cityNames.sort();
      
      setCities(cityNames);
      setFilteredCities(cityNames);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCityError('Failed to load cities. Please try again.');
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };
  
  // Debounced city search
  useEffect(() => {
    if (!isHospitalType) {
      setCities([]);
      setFilteredCities([]);
      setCitySearchText('');
      return;
    }
    
    // Clear previous timeout
    if (citySearchTimeoutRef.current) {
      clearTimeout(citySearchTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    citySearchTimeoutRef.current = setTimeout(() => {
      fetchCities(citySearchText);
    }, 300);
    
    return () => {
      if (citySearchTimeoutRef.current) {
        clearTimeout(citySearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySearchText, isHospitalType]);

  // Filter cities based on search text
  useEffect(() => {
    if (!citySearchText.trim()) {
      setFilteredCities(cities);
    } else {
      const filtered = cities.filter(city =>
        city.toLowerCase().includes(citySearchText.toLowerCase())
      );
      setFilteredCities(filtered);
    }
  }, [citySearchText, cities]);

  // Filter providers based on search text
  useEffect(() => {
    if (!providerSearchText.trim()) {
      setFilteredProviders(serviceProviders);
    } else {
      const filtered = serviceProviders.filter(provider =>
        provider.name.toLowerCase().includes(providerSearchText.toLowerCase()) ||
        (provider.address && provider.address.toLowerCase().includes(providerSearchText.toLowerCase()))
      );
      setFilteredProviders(filtered);
    }
  }, [providerSearchText, serviceProviders]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // City dropdown
      if (
        cityInputRef.current &&
        cityDropdownRef.current &&
        !cityInputRef.current.contains(event.target) &&
        !cityDropdownRef.current.contains(event.target)
      ) {
        setShowCityDropdown(false);
      }
      
      // Provider dropdown
      if (
        providerInputRef.current &&
        providerDropdownRef.current &&
        !providerInputRef.current.contains(event.target) &&
        !providerDropdownRef.current.contains(event.target)
      ) {
        setShowProviderDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch hospitals by city (for hospital types)
  const fetchHospitals = async (city, nameFilter = '') => {
    if (!city) {
      setServiceProviders([]);
      setProviderError(null);
      return;
    }

    setLoadingProviders(true);
    setProviderError(null);
    setServiceProviders([]);

    try {
      const response = await getHospitalsByCity(city, '', nameFilter, currentRole);
      
      // Handle different response structures
      let hospitalsList = [];
      if (response && response.metadata && response.metadata.api_status === true) {
        hospitalsList = response.msg?.items || response.msg || [];
      } else if (response?.items && Array.isArray(response.items)) {
        hospitalsList = response.items;
      } else if (Array.isArray(response)) {
        hospitalsList = response;
      } else if (response?.msg && Array.isArray(response.msg)) {
        hospitalsList = response.msg;
      } else if (response?.data && Array.isArray(response.data)) {
        hospitalsList = response.data;
      }
      
      // Format hospitals for dropdown (expecting objects with id and name)
      const formattedHospitals = hospitalsList.map((hospital) => {
        if (typeof hospital === 'object' && hospital !== null) {
          return {
            id: hospital.id || hospital.hospital_id,
            name: hospital.name || hospital.hospital_name || 'Unknown Hospital',
            city: hospital.city || '',
            state: hospital.state || '',
            address: hospital.address_line || ''
          };
        }
        return {
          id: String(hospital),
          name: String(hospital)
        };
      }).filter(h => h.id); // Filter out hospitals without IDs
      
      setServiceProviders(formattedHospitals);
      setFilteredProviders(formattedHospitals);
      
      // Auto-select if only one hospital
      if (formattedHospitals.length === 1) {
        setFormData(prev => ({ ...prev, serviceProvider: formattedHospitals[0].id }));
        setProviderSearchText(formattedHospitals[0].name);
      }
    } catch (error) {
      console.error('Error fetching hospitals:', error);
      setProviderError('Failed to load hospitals. Please try again.');
      setServiceProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  };

  // Fetch service providers from API (for non-hospital types)
  const fetchServiceProviders = async (serviceType) => {
    if (!serviceType) {
      setServiceProviders([]);
      setProviderError(null);
      return;
    }

    // Don't fetch if it's a hospital type (will be handled by fetchHospitals)
    if (serviceType === 'hospitalization' || serviceType === 'hospital_cash_allowance') {
      return;
    }

    setLoadingProviders(true);
    setProviderError(null);
    setServiceProviders([]);

    try {
      const response = await getServiceProviders(serviceType);
      
      if (response && response.metadata && response.metadata.api_status === true) {
        const providers = response.msg?.items || [];
        setServiceProviders(providers);
        
        // Auto-select if only one provider
        if (providers.length === 1) {
          setFormData(prev => ({ ...prev, serviceProvider: providers[0].id }));
        }
      } else {
        const errorMsg = response?.msg?.error || 'Failed to fetch service providers';
        setProviderError(errorMsg);
        setServiceProviders([]);
      }
    } catch (error) {
      console.error('Error fetching service providers:', error);
      setProviderError('Failed to load service providers. Please try again.');
      setServiceProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If service type changes, reset all dependent fields
    if (name === 'serviceType') {
        const isHospital = value === 'hospitalization' || value === 'hospital_cash_allowance';
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        selectedCity: '',       // Reset city
        serviceProvider: ''     // Reset service provider
      }));
      
      // Reset cities and hospitals
      setCities([]);
        setFilteredCities([]);
      setServiceProviders([]);
        setFilteredProviders([]);
      setCityError(null);
      setProviderError(null);
        setCitySearchText('');
        setProviderSearchText('');
        setShowCityDropdown(false);
        setShowProviderDropdown(false);
      
      // For hospital types, fetch cities list
      if (isHospital) {
          fetchCities('');
    } else {
        // Fetch service providers for non-hospital types
        fetchServiceProviders(value);
      }
    } 
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.serviceType) {
      errors.serviceType = 'Service type is required';
    }
    
    // For hospital types, validate city
    if (isHospitalType) {
      if (!formData.selectedCity) {
        errors.selectedCity = 'City is required';
      }
    }
    
    if (!formData.redeemableCashback || parseFloat(formData.redeemableCashback) < 0) {
      errors.redeemableCashback = 'Valid redeemable cashback is required';
    }
    
    if (!formData.thirdPartyTransactionId?.trim()) {
      errors.thirdPartyTransactionId = 'Third party transaction id is required';
    }
    
    if (!formData.serviceProvider?.trim()) {
      errors.serviceProvider = 'Service provider is required';
    }
    
    if (!formData.ayushpayCommission || parseFloat(formData.ayushpayCommission) < 0) {
      errors.ayushpayCommission = 'Valid Ayushpay commission is required';
    }

    if (!formData.price || parseFloat(formData.price) < 0) {
      errors.price = 'Valid price is required';
    }

    if (formData.ayushpayCommission && formData.price && parseFloat(formData.ayushpayCommission) > parseFloat(formData.price)) {
      errors.ayushpayCommission = 'Ayushpay commission cannot be greater than price';
    }

    if (formData.redeemableCashback && formData.price && parseFloat(formData.redeemableCashback) > parseFloat(formData.price)) {
      errors.redeemableCashback = 'Redeemable cashback cannot be greater than price';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Map form data to API request format
      // Service types match backend choices exactly
      const serviceTypeMap = {
        'medicine': 'medicine',
        'diagnostics': 'diagnostics',
        'hospitalization': 'hospitalization',
        'loan': 'loan',
        'hospital_cash_allowance': 'hospital_cash_allowance'
      };
      
      // Find the provider name from the selected provider ID
      const selectedProvider = serviceProviders.find(p => p.id === formData.serviceProvider);
      const providerName = selectedProvider ? selectedProvider.name : formData.serviceProvider.trim();
      
      // API automatically gets wallet_id from application, no need to send it
      // service_provider should be the provider name (not UUID)
      const apiData = {
        service_type: serviceTypeMap[formData.serviceType] || formData.serviceType.toLowerCase(),
        service_provider: providerName, // Provider name, not UUID
        cashback: parseFloat(formData.redeemableCashback),
        ayushpay_commission: parseFloat(formData.ayushpayCommission),
        price: parseFloat(formData.price),
        third_party_txn_no: formData.thirdPartyTransactionId.trim()
      };

      // Hospitalization transactions need the hospital's id so the backend can
      // link a HospitalBooking record — without it the transaction is created
      // but never appears in the Hospital Payments module.
      if (formData.serviceType === 'hospitalization' && selectedProvider) {
        apiData.hospital_id = selectedProvider.id;
      }

      // Call the API
      const response = await createManualTransaction(id, currentRole, apiData);
      
      // Handle response - API may return data in different structures
      // Finance and SuperAdmin API returns: { id, wallet_id, service_type, service_provider, price, cashback, ayushpay_commission, third_party_txn_no, status, created_at }
      let successMessage = 'Transaction created successfully!';
      let transactionId = null;
      
      // Check for direct response (Finance/SuperAdmin format)
      if (response.id) {
        transactionId = response.id;
      } 
      // Check for nested msg structure (Ops format)
      else if (response.msg && response.msg.id) {
        transactionId = response.msg.id;
      }
      // Check for nested data structure
      else if (response.data && response.data.id) {
        transactionId = response.data.id;
      }
      
      if (transactionId) {
        successMessage = `Transaction created successfully! Transaction ID: ${transactionId}`;
      }
      
      // Show success notification
      showNotification(successMessage, 'success');
      
      // Reset form
      setFormData({
        serviceType: '',
        selectedCity: '',
        redeemableCashback: '',
        thirdPartyTransactionId: '',
        serviceProvider: '',
        ayushpayCommission: '',
        price: ''
      });
      
      // Clear service providers, cities state
      setServiceProviders([]);
      setFilteredProviders([]);
      setProviderError(null);
      setProviderSearchText('');
      setShowProviderDropdown(false);
      setCities([]);
      setFilteredCities([]);
      setCitySearchText('');
      setShowCityDropdown(false);
      setCityError(null);
      
      // Navigate to subscriber wallet profile page after success
      window.location.href = `/subscriber-wallet/subscriber/${encodeURIComponent(id)}`;
      
    } catch (error) {
      // Parse error message
      let errorMessage = 'Failed to create transaction. Please try again.';
      
      if (error.message) {
        // Try to extract error details from API response
        try {
          const errorMatch = error.message.match(/\{[\s\S]*\}/);
          if (errorMatch) {
            const errorData = JSON.parse(errorMatch[0]);
            
            // Check for msg.error structure (backend error format)
            if (errorData.msg && errorData.msg.error) {
              errorMessage = errorData.msg.error;
            } 
            // Check for direct error field
            else if (errorData.error) {
              errorMessage = errorData.error;
            } 
            // Check for validation errors with details
            else if (errorData.details) {
              const detailMessages = Object.values(errorData.details).flat();
              errorMessage = `Validation Error: ${detailMessages.join(', ')}`;
            }
            // Check for msg with nested error structure
            else if (errorData.msg && typeof errorData.msg === 'object') {
              // Try to find any error message in msg object
              const msgKeys = Object.keys(errorData.msg);
              if (msgKeys.length > 0) {
                const firstKey = msgKeys[0];
                if (typeof errorData.msg[firstKey] === 'string') {
                  errorMessage = errorData.msg[firstKey];
                } else if (Array.isArray(errorData.msg[firstKey])) {
                  errorMessage = errorData.msg[firstKey].join(', ');
                }
              }
            }
          } else if (error.message.includes('Wallet not found')) {
            errorMessage = 'Wallet not found for this application. Please ensure the subscriber has an active wallet.';
          } else if (error.message.includes('timeout')) {
            errorMessage = 'Request timeout. Please check your connection and try again.';
          } else {
            errorMessage = error.message;
          }
        } catch (parseError) {
          // If parsing fails, check for common error patterns in the message
          if (error.message.includes('Wallet not found')) {
            errorMessage = 'Wallet not found for this application. Please ensure the subscriber has an active wallet.';
          } else if (error.message.includes('timeout')) {
            errorMessage = 'Request timeout. Please check your connection and try again.';
          } else if (error.message.includes('422')) {
            errorMessage = 'Validation error. Please check all fields and try again.';
          } else {
            errorMessage = error.message;
          }
        }
      }
      
      // Show error notification
      showNotification(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load profile data from API
  useEffect(() => {
    const loadData = async () => {
      if (isLoadingDataRef.current) return;
      
      try {
        setLoading(true);
        setError(null);
        
        if (!id) {
          setError('Subscriber ID is required');
          setLoading(false);
          return;
        }
        
        if (!currentRole) {
          setError('Invalid role access');
          setLoading(false);
          return;
        }
        
        isLoadingDataRef.current = true;
        
        const profileData = await getSubscriberProfile(id, currentRole);
        
        if (profileData) {
          let profileToSet = null;
          
          // Handle different API response structures
          if (profileData.msg && typeof profileData.msg === 'object') {
            profileToSet = {
              id: profileData.msg.id || id,
              personal: profileData.msg.personal || {},
              subscription: profileData.msg.subscription || profileData.msg.subscription_id || '',
              valid_period: profileData.msg.valid_period || profileData.msg.validity_period || '',
              plan: profileData.msg.plan || profileData.msg.personal_subscription || profileData.msg.subscription_plan || '',
              status: profileData.msg.status || '',
              kyc: profileData.msg.kyc || profileData.msg.kyc_status || ''
            };
          } else if (profileData.data && typeof profileData.data === 'object') {
            profileToSet = {
              id: profileData.data.id || id,
              personal: profileData.data.personal || {},
              subscription: profileData.data.subscription || profileData.data.subscription_id || '',
              valid_period: profileData.data.valid_period || profileData.data.validity_period || '',
              plan: profileData.data.plan || profileData.data.personal_subscription || profileData.data.subscription_plan || '',
              status: profileData.data.status || '',
              kyc: profileData.data.kyc || profileData.data.kyc_status || ''
            };
          } else if (typeof profileData === 'object') {
            profileToSet = {
              id: profileData.id || id,
              personal: profileData.personal || {},
              subscription: profileData.subscription || profileData.subscription_id || '',
              valid_period: profileData.valid_period || profileData.validity_period || '',
              plan: profileData.plan || profileData.personal_subscription || profileData.subscription_plan || '',
              status: profileData.status || '',
              kyc: profileData.kyc || profileData.kyc_status || ''
            };
          }
          
          if (profileToSet) {
            setProfile(profileToSet);
          }
        }
      } catch (error) {
        setError('Failed to load subscriber profile');
      } finally {
        setLoading(false);
        isLoadingDataRef.current = false;
      }
    };

    loadData();
  }, [id, currentRole]);

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  // Check access for manual transaction (Ops, Finance, and SuperAdmin)
  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access Manual Transaction. This feature is only available for Ops, Finance, and SuperAdmin roles.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/subscriber-wallet/manual-transaction')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="text-gray-600 text-lg font-semibold mb-2">
            Profile Data Not Found
          </div>
          <button 
            onClick={() => navigate('/subscriber-wallet/manual-transaction')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => navigate('/subscriber-wallet')} 
              className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
            >
              ← Back to Home
            </button>
          <button 
            onClick={() => navigate('/subscriber-wallet/manual-transaction')} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Search
          </button>
          </div>
        </div>

        {/* Page Heading */}
        <div className="mt-2 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Manual Transaction - Subscriber Profile</h1>
          <p className="text-gray-500">Subscriber information for manual transaction processing</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm">
          <div className="space-y-6">
            {/* Header Section */}
            <div className="relative">
              <div>
                <div className="text-xl font-bold text-gray-900">{profile?.personal?.full_name || profile?.name || 'Subscriber'}</div>
                <div className="text-sm text-gray-600 flex items-center space-x-2 mt-1">
                  <span>Mobile No: {(isFinance || isSupport || isSuperAdmin) ? (profile?.personal?.mobile || profile?.phoneFull) : (showPhone ? (profile?.personal?.mobile || profile?.phoneFull) : maskedPhone)}</span>
                  {!(isFinance || isSupport || isSuperAdmin) && (
                    <button
                      onClick={() => setShowPhone(v => !v)}
                      className="text-gray-400 hover:text-gray-600"
                      aria-label={showPhone ? 'Hide phone' : 'Show phone'}
                    >
                      {showPhone ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Details Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">REF CODE</div>
                  <div className="text-sm font-medium text-gray-900">{profile?.subscription || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Validity Period</div>
                  <div className="text-sm font-medium text-gray-900">
                    {(() => {
                      const validPeriod = profile?.valid_period;
                      if (!validPeriod) return 'N/A';
                      
                      // Handle object with start_date and end_date
                      if (typeof validPeriod === 'object' && validPeriod !== null && validPeriod.start_date && validPeriod.end_date) {
                        const startFormatted = formatDate(validPeriod.start_date);
                        const endFormatted = formatDate(validPeriod.end_date);
                        return `${startFormatted} - ${endFormatted}`;
                      }
                      
                      // Handle string format
                      return formatDate(validPeriod);
                    })()}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Plan</div>
                  <div className="text-sm font-medium text-gray-900">{profile?.plan || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">KYC Status</div>
                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-md bg-green-500 text-white w-fit">{profile?.kyc || 'N/A'}</span>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-md w-fit ${
                    profile?.status === 'Active' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
                  }`}>
                    {profile?.status || 'N/A'}
                  </span>
                </div>
                <div></div>
              </div>
            </div>
          </div>
        </div>

        {/* Manual Transaction Form */}
        <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-6 shadow-sm">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Transaction</h2>
            <p className="text-sm text-gray-500 mt-1">Fill in the details to create a new transaction</p>
          </div>


          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Service Type */}
              <div>
                <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    formErrors.serviceType ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select service type</option>
                  <option value="medicine">Medicine</option>
                  <option value="diagnostics">Diagnostics</option>
                  <option value="hospitalization">Hospitalization</option>
                  <option value="loan">Loan</option>
                  <option value="hospital_cash_allowance">Hospital Cash Allowance</option>
                </select>
                {formErrors.serviceType && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.serviceType}</p>
                )}
              </div>

              {/* City (only for hospital types) */}
              {isHospitalType && (
              <div>
                  <label htmlFor="citySearch" className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                </label>
                  <div className="relative" ref={cityInputRef}>
                    <input
                      type="text"
                      id="citySearch"
                      value={formData.selectedCity || citySearchText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCitySearchText(value);
                        setShowCityDropdown(true);
                        // Clear selected city if user is typing
                        if (formData.selectedCity && value !== formData.selectedCity) {
                          setFormData(prev => ({ ...prev, selectedCity: '' }));
                        }
                      }}
                      onFocus={() => setShowCityDropdown(true)}
                      placeholder={loadingCities ? 'Loading cities...' : 'Search and select city...'}
                    disabled={loadingCities}
                  className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      formErrors.selectedCity ? 'border-red-500' : 'border-gray-300'
                    } ${loadingCities ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    {showCityDropdown && filteredCities.length > 0 && (
                      <div
                        ref={cityDropdownRef}
                        className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto overflow-x-hidden"
                        style={{ maxHeight: '240px' }}
                      >
                        {filteredCities.map((city, index) => (
                          <div
                            key={`${city}-${index}`}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, selectedCity: city }));
                              setCitySearchText(city);
                              setShowCityDropdown(false);
                              // Fetch hospitals for selected city
                              fetchHospitals(city);
                            }}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                        {city}
                          </div>
                    ))}
                      </div>
                    )}
                    {showCityDropdown && filteredCities.length === 0 && !loadingCities && citySearchText && (
                      <div
                        ref={cityDropdownRef}
                        className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                      >
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          No cities found
                        </div>
                      </div>
                    )}
                  </div>
                  {cityError && (
                    <p className="mt-1 text-sm text-red-600">{cityError}</p>
                  )}
                  {formErrors.selectedCity && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.selectedCity}</p>
                  )}
                  {!loadingCities && cities.length === 0 && isHospitalType && !cityError && (
                    <p className="mt-1 text-sm text-gray-500">No cities available</p>
                )}
              </div>
              )}

              {/* Service Provider */}
              <div>
                <label htmlFor="serviceProvider" className="block text-sm font-medium text-gray-700 mb-2">
                  {isHospitalType ? 'Hospital' : 'Service Provider'} <span className="text-red-500">*</span>
                </label>
                {isHospitalType ? (
                  // Searchable dropdown for hospitals
                  <div className="relative" ref={providerInputRef}>
                    <input
                      type="text"
                  id="serviceProvider"
                      value={(() => {
                        const selectedProvider = serviceProviders.find(p => p.id === formData.serviceProvider);
                        return selectedProvider ? selectedProvider.name : providerSearchText;
                      })()}
                      onChange={(e) => {
                        const value = e.target.value;
                        setProviderSearchText(value);
                        setShowProviderDropdown(true);
                        // Clear selected provider if user is typing
                        if (formData.serviceProvider) {
                          const selectedProvider = serviceProviders.find(p => p.id === formData.serviceProvider);
                          if (selectedProvider && value !== selectedProvider.name) {
                            setFormData(prev => ({ ...prev, serviceProvider: '' }));
                          }
                        }
                      }}
                      onFocus={() => {
                        if (serviceProviders.length > 0) {
                          setShowProviderDropdown(true);
                        }
                      }}
                      placeholder={
                        loadingProviders 
                          ? 'Loading hospitals...' 
                          : !formData.serviceType
                            ? 'Select service type first'
                            : !formData.selectedCity
                              ? 'Select city first'
                              : serviceProviders.length === 0 && !providerError
                                ? 'No hospitals available'
                                : 'Search and select hospital...'
                      }
                  disabled={
                    !formData.serviceType || 
                    loadingProviders || 
                    (isHospitalType && (!formData.selectedCity))
                  }
                  className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    formErrors.serviceProvider ? 'border-red-500' : 'border-gray-300'
                  } ${(!formData.serviceType || loadingProviders || (isHospitalType && !formData.selectedCity)) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    {showProviderDropdown && filteredProviders.length > 0 && (
                      <div
                        ref={providerDropdownRef}
                        className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto overflow-x-hidden"
                        style={{ maxHeight: '240px' }}
                      >
                        {filteredProviders.map((provider) => (
                          <div
                            key={provider.id}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, serviceProvider: provider.id }));
                              setProviderSearchText(provider.name);
                              setShowProviderDropdown(false);
                            }}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="font-medium">{provider.name}</div>
                            {provider.address && (
                              <div className="text-sm text-gray-500 mt-0.5">{provider.address}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {showProviderDropdown && filteredProviders.length === 0 && !loadingProviders && providerSearchText && (
                      <div
                        ref={providerDropdownRef}
                        className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                      >
                        <div className="px-4 py-2 text-gray-500 text-sm">
                          No hospitals found
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Regular select for non-hospital types
                  <select
                    id="serviceProvider"
                    name="serviceProvider"
                    value={formData.serviceProvider}
                    onChange={handleInputChange}
                    disabled={!formData.serviceType || loadingProviders}
                    className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      formErrors.serviceProvider ? 'border-red-500' : 'border-gray-300'
                    } ${(!formData.serviceType || loadingProviders) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">
                    {loadingProviders 
                      ? 'Loading providers...' 
                      : !formData.serviceType
                        ? 'Select service type first'
                        : serviceProviders.length === 0 && !providerError
                          ? 'No providers available'
                          : 'Select service provider'}
                  </option>
                  {serviceProviders.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
                )}
                {providerError && (
                  <p className="mt-1 text-sm text-red-600">{providerError}</p>
                )}
                {formErrors.serviceProvider && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.serviceProvider}</p>
                )}
                {!loadingProviders && serviceProviders.length === 0 && formData.serviceType && !providerError && (
                  <p className="mt-1 text-sm text-gray-500">
                    {isHospitalType ? 'No hospitals found for this city' : 'No service providers found for this service type'}
                  </p>
                )}
              </div>

              {/* Redeemable Cashback */}
              <div>
                <label htmlFor="redeemableCashback" className="block text-sm font-medium text-gray-700 mb-2">
                  Redeemable Cashback (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="redeemableCashback"
                  name="redeemableCashback"
                  value={formData.redeemableCashback}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    formErrors.redeemableCashback ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.redeemableCashback && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.redeemableCashback}</p>
                )}
              </div>

              {/* Third Party Transaction ID */}
              <div>
                <label htmlFor="thirdPartyTransactionId" className="block text-sm font-medium text-gray-700 mb-2">
                  Third Party Transaction ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="thirdPartyTransactionId"
                  name="thirdPartyTransactionId"
                  value={formData.thirdPartyTransactionId}
                  onChange={handleInputChange}
                  placeholder="Enter third party transaction id"
                  className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    formErrors.thirdPartyTransactionId ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.thirdPartyTransactionId && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.thirdPartyTransactionId}</p>
                )}
              </div>

              {/* Ayushpay Commission */}
              <div>
                <label htmlFor="ayushpayCommission" className="block text-sm font-medium text-gray-700 mb-2">
                  Ayushpay Commission (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="ayushpayCommission"
                  name="ayushpayCommission"
                  value={formData.ayushpayCommission}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    formErrors.ayushpayCommission ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.ayushpayCommission && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.ayushpayCommission}</p>
                )}
              </div>

              {/* Price */}
              <div>
                <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                  Price (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    formErrors.price ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.price && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.price}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-2.5 rounded-md font-medium text-white transition-colors ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </span>
                ) : (
                  'Create Transaction'
                )}
              </button>
            </div>
          </form>
        </div>
        
        {/* Notification Toast */}
        {mounted && notification && createPortal(
          <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center z-[10000] pointer-events-none">
            <div className={`pointer-events-auto transform transition-all duration-300 ease-out ${
              notification ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'
            }`}>
              <div className={`flex items-center space-x-3 px-6 py-4 rounded-lg shadow-2xl border-2 min-w-[320px] max-w-md ${
                notification.type === 'success' 
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 text-green-800' 
                  : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300 text-red-800'
              }`}>
                <div className={`flex-shrink-0 ${
                  notification.type === 'success' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {notification.type === 'success' ? (
                    <FaCheckCircle className="w-6 h-6" />
                  ) : (
                    <FaExclamationCircle className="w-6 h-6" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold leading-relaxed">
                    {notification.message}
                  </p>
                </div>
                <button
                  onClick={() => setNotification(null)}
                  className={`flex-shrink-0 ml-2 p-1 rounded-full hover:bg-opacity-20 transition-colors ${
                    notification.type === 'success' ? 'text-green-600 hover:bg-green-600' : 'text-red-600 hover:bg-red-600'
                  }`}
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default ManualTransactionProfile;

